import type { SupportTicket, TicketMessage, TicketStats } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.gamaleigan.is';

let csrfToken: string | null = null;
let tokenExpiry = 0;
let tokenFetchPromise: Promise<string> | null = null;

async function getCsrfToken(): Promise<string> {
  if (csrfToken && Date.now() < tokenExpiry) {
    return csrfToken;
  }

  if (!tokenFetchPromise) {
    tokenFetchPromise = (async () => {
      const response = await fetch(`${API_URL}/api/auth/csrf-token`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const data = await response.json() as { token?: string; expiresAt?: number };
      csrfToken = data.token || null;
      tokenExpiry = data.expiresAt || (Date.now() + 10 * 60 * 1000);

      if (!csrfToken) {
        throw new Error('CSRF token missing from response');
      }

      return csrfToken;
    })().finally(() => {
      tokenFetchPromise = null;
    });
  }

  return tokenFetchPromise;
}

function invalidateCsrfToken(): void {
  csrfToken = null;
  tokenExpiry = 0;
}

export async function initializeCsrfToken(): Promise<void> {
  await getCsrfToken();
}

export class SupportApi {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOnCsrf = true
  ): Promise<T> {
    const token = localStorage.getItem('token');
    const method = (options.method || 'GET').toUpperCase();
    const isMutating = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(options.headers as Record<string, string>),
    };

    if (isMutating) {
      const csrf = await getCsrfToken();
      headers['X-CSRF-Token'] = csrf;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      const errorMessage = error.error || `HTTP ${response.status}`;

      if (response.status === 403 && retryOnCsrf && String(errorMessage).toLowerCase().includes('csrf')) {
        invalidateCsrfToken();
        return this.request(endpoint, options, false);
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  // List tickets with filters
  async listTickets(params?: {
    status?: string;
    priority?: string;
    category?: string;
    assigned_to?: string;
  }): Promise<{ tickets: SupportTicket[] }> {
    const queryParams = new URLSearchParams(params as Record<string, string>);
    const query = queryParams.toString();
    return this.request(`/api/þjonustuver${query ? `?${query}` : ''}`);
  }

  // Get ticket details with messages
  async getTicket(id: string): Promise<{
    ticket: SupportTicket;
    messages: TicketMessage[];
  }> {
    return this.request(`/api/þjonustuver/${id}`);
  }

  // Create new ticket
  async createTicket(data: {
    customer_id?: string;
    customer_email?: string;
    customer_name?: string;
    subject: string;
    description: string;
    priority?: string;
    category?: string;
    attachment_url?: string;
  }): Promise<{ success: boolean; ticket: SupportTicket; message: string }> {
    return this.request('/api/þjonustuver', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Update ticket
  async updateTicket(
    id: string,
    data: {
      status?: string;
      priority?: string;
      category?: string;
      assigned_to?: string | null;
    }
  ): Promise<{ success: boolean; ticket: SupportTicket }> {
    return this.request(`/api/þjonustuver/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Add message/reply
  async addMessage(
    id: string,
    message: string,
    isInternal = false
  ): Promise<{ success: boolean; messages: TicketMessage[] }> {
    return this.request(`/api/þjonustuver/${id}/svara`, {
      method: 'POST',
      body: JSON.stringify({ message, is_internal: isInternal ? 1 : 0 }),
    });
  }

  // Get stats (to be implemented in backend)
  async getStats(): Promise<TicketStats> {
    return this.request('/api/þjonustuver/stats');
  }
}

export const supportApi = new SupportApi();
